import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FaqCoverageTopic, FaqDiagnostic, FaqSeverity } from '../../types/faq-monitor.js';
import { DispositionControl } from './DispositionControl.js';
import {
  SEVERITY_FILTERS,
  SeverityCountChip,
  VerdictChip,
  severityWeight,
} from './SeverityBadges.js';

/**
 * 外部リンク。URL が無ければ何も描かない。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business FAQ記事・実装仕様書への外部リンクを表示する
 * @graph-connects none
 */
export function SourceLink({ url, labelKey }: { url: string | null; labelKey: string }) {
  const { t } = useTranslation();
  if (url === null) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-xs text-blue-600 underline dark:text-blue-400"
    >
      {t(labelKey)}
      <ExternalLink className="h-3 w-3" aria-hidden="true" />
    </a>
  );
}

/**
 * 引用の開閉ボタン。
 *
 * 行全体を開閉ボタンにすると、メモを触ろうとして誤って開閉してしまう。
 * 開閉の当たり判定はこのボタンだけに閉じる。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 引用の開閉だけを担うボタンを提供し、行全体の誤操作を防ぐ
 * @graph-connects none
 */
export function QuoteToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="text-xs text-blue-600 underline dark:text-blue-400"
    >
      {open ? t('faqMonitor.hideQuotes') : t('faqMonitor.showQuotes')}
    </button>
  );
}

/**
 * 乖離の指摘1件。
 *
 * 記事名は親（`ArticleGroup`）が出すので、ここでは繰り返さない。
 * 見出しは「記事のどの記述か（主張）」で、直す対象そのものを指す。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 記事の主張を見出しに、食い違いの中身・引用・対応方針を1件分表示する
 * @graph-connects VerdictChip [calls] 判定チップの表示
 * @graph-connects DispositionControl [calls] 対応方針の表示と編集
 */
export function DiagnosticItem({ diagnostic }: { diagnostic: FaqDiagnostic }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <li className="border-b border-border-subtle py-3 last:border-b-0">
      <div className="flex items-start gap-2">
        <VerdictChip verdict={diagnostic.verdict} severity={diagnostic.severity} />
        <p className="min-w-0 flex-1 font-medium text-foreground">
          {diagnostic.claim ?? diagnostic.articleTitle}
        </p>
      </div>
      <p className="mt-1 text-sm text-foreground-secondary">{diagnostic.finding}</p>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <QuoteToggle
          open={open}
          onToggle={() => {
            setOpen((prev) => !prev);
          }}
        />
        <SourceLink url={diagnostic.articleUrl} labelKey="faqMonitor.openArticle" />
        <SourceLink url={diagnostic.specUrl} labelKey="faqMonitor.openSpec" />
        <div className="ml-auto">
          <DispositionControl hash={diagnostic.findingHash} disposition={diagnostic.disposition} />
        </div>
      </div>

      {open && (
        <div className="mt-3 space-y-3 pl-3">
          <div>
            <p className="text-xs font-medium text-foreground-muted">
              {t('faqMonitor.evidenceFaq')}
            </p>
            <blockquote className="mt-1 border-l-2 border-border pl-3 text-sm whitespace-pre-wrap text-foreground-secondary">
              {diagnostic.evidenceFaq ?? t('faqMonitor.noEvidence')}
            </blockquote>
          </div>
          <div>
            <p className="text-xs font-medium text-foreground-muted">
              {t('faqMonitor.evidenceSpec')}
            </p>
            <blockquote className="mt-1 border-l-2 border-border pl-3 text-sm whitespace-pre-wrap text-foreground-secondary">
              {diagnostic.evidenceSpec ?? t('faqMonitor.noEvidence')}
            </blockquote>
          </div>
          <p className="text-xs text-foreground-muted">
            {diagnostic.specPath ?? t('faqMonitor.noSpec')}
            {diagnostic.specVerifiedAt !== null &&
              ` / ${t('faqMonitor.specVerifiedAt', { date: diagnostic.specVerifiedAt })}`}
          </p>
        </div>
      )}
    </li>
  );
}

/**
 * 記事1本ぶんの指摘をまとめた開閉ブロック。
 *
 * 直す作業は記事単位なので、同じ記事の指摘は一度に見えたほうがよい。
 * 平坦に並べると、実データでは同じ記事名が6回続いて読めなくなる。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 記事ごとに指摘をまとめ、件数と重大度の内訳を見出しに出す
 * @graph-connects SeverityCountChip [calls] 重大度別件数チップの表示
 * @graph-connects DiagnosticItem [calls] 指摘1件の表示
 */
export function ArticleGroup({
  articleTitle,
  diagnostics,
}: {
  articleTitle: string;
  diagnostics: FaqDiagnostic[];
}) {
  const [open, setOpen] = useState(true);
  const Caret = open ? ChevronDown : ChevronRight;
  const counts = SEVERITY_FILTERS.map((severity) => ({
    severity,
    count: diagnostics.filter((d) => d.severity === severity).length,
  })).filter((c) => c.count > 0);

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <button
        type="button"
        onClick={() => {
          setOpen((prev) => !prev);
        }}
        aria-expanded={open}
        className="flex w-full items-center gap-2 bg-background-secondary px-3 py-2 text-left"
      >
        <Caret className="h-4 w-4 shrink-0 text-foreground-muted" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate font-semibold text-foreground">
          {articleTitle}
        </span>
        {counts.map((c) => (
          <SeverityCountChip key={c.severity} severity={c.severity} count={c.count} />
        ))}
      </button>
      {open && (
        <ul className="px-3">
          {diagnostics.map((diagnostic) => (
            <DiagnosticItem key={diagnostic.findingHash} diagnostic={diagnostic} />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * カバレッジの指摘1件。実際の問い合わせを引用して示す（US-8）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 聞かれているのに書かれていない／伝わっていない論点を、引用・リンク・対応方針つきで表示する
 * @graph-connects DispositionControl [calls] 対応方針の表示と編集
 */
export function CoverageItem({ topic }: { topic: FaqCoverageTopic }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <li className="border-b border-border-subtle py-3 last:border-b-0">
      <div className="flex items-start gap-2">
        <span className="shrink-0 rounded border border-border bg-background-secondary px-2 py-0.5 text-xs font-medium tabular-nums text-foreground-secondary">
          {t('faqMonitor.inquiryCount', { count: topic.inquiryCount })}
        </span>
        <p className="min-w-0 flex-1 font-medium text-foreground">{topic.topic}</p>
        <span className="shrink-0 text-xs text-foreground-muted">{topic.coverage}</span>
      </div>
      <p className="mt-1 text-sm text-foreground-secondary">{topic.suggestion}</p>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <QuoteToggle
          open={open}
          onToggle={() => {
            setOpen((prev) => !prev);
          }}
        />
        <SourceLink url={topic.matchedArticleUrl} labelKey="faqMonitor.openArticle" />
        <div className="ml-auto">
          <DispositionControl hash={topic.topicHash} disposition={topic.disposition} />
        </div>
      </div>

      {open && (
        <div className="mt-3 space-y-3 pl-3">
          <p className="text-sm text-foreground-secondary">
            {topic.coverage === '記事なし'
              ? t('faqMonitor.noArticle')
              : t('faqMonitor.notReaching', {
                  title: topic.matchedArticleTitle ?? topic.matchedArticleId ?? '',
                })}
          </p>
          <div>
            <p className="text-xs font-medium text-foreground-muted">
              {t('faqMonitor.evidenceInquiries')}
            </p>
            <ul className="mt-1 space-y-1">
              {topic.evidenceInquiries.length === 0 ? (
                <li className="text-sm text-foreground-muted">{t('faqMonitor.noEvidence')}</li>
              ) : (
                topic.evidenceInquiries.map((inquiry) => (
                  <li
                    key={inquiry}
                    className="border-l-2 border-border pl-3 text-sm whitespace-pre-wrap text-foreground-secondary"
                  >
                    {inquiry}
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * 指摘を記事ごとにまとめる。並び順は最も重い指摘が先。
 *
 * 束ねるキーは `articleId`。`articleTitle` は Channel Talk の自由記入見出しで
 * 一意性の保証が無く、同名の記事が2本あると別記事の指摘が1つのブロックに混ざる。
 * 見出しに出す文字列だけ、グループ内の代表要素のタイトルを使う。
 *
 * 記事の中では重大度順にし、記事どうしは「その記事が抱える最も重い重大度」で並べる。
 * 件数の多さで並べると、軽い指摘が沢山ある記事が「高」1件の記事より上に来る。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 指摘を記事IDごとに束ね、重い指摘を抱える記事から順に並べる
 * @graph-connects severityWeight [calls] 重大度の並べ替え重み
 */
export function groupByArticle(
  diagnostics: FaqDiagnostic[],
): { articleId: string; articleTitle: string; diagnostics: FaqDiagnostic[] }[] {
  const groups = new Map<string, FaqDiagnostic[]>();
  for (const diagnostic of diagnostics) {
    const bucket = groups.get(diagnostic.articleId) ?? [];
    bucket.push(diagnostic);
    groups.set(diagnostic.articleId, bucket);
  }

  const worst = (items: FaqDiagnostic[]): number =>
    Math.min(...items.map((d: { severity: FaqSeverity | null }) => severityWeight(d.severity)));

  return [...groups.entries()]
    .map(([articleId, items]) => {
      const sorted = [...items].sort(
        (a, b) => severityWeight(a.severity) - severityWeight(b.severity),
      );
      return {
        articleId,
        articleTitle: sorted[0]?.articleTitle ?? articleId,
        diagnostics: sorted,
      };
    })
    .sort(
      (a, b) =>
        worst(a.diagnostics) - worst(b.diagnostics) || b.diagnostics.length - a.diagnostics.length,
    );
}
